// Algo Sentiment Board — renders the engine's server-derived closed-vocabulary
// `display_state` 1:1. The SPA is a dumb renderer: it NEVER classifies from
// note text and NEVER invents states. "Unknown" is not a display state — any
// string outside the vocabulary renders as Blocked (loud fault), per the
// engine contract in crates/server/src/eval_vocab.rs.
//
// Display states (fixed order, fixed color):
//   long/short  LIVE         bright green — in a position
//   managing    MANAGING     bright green — in position, exit management active
//   exiting     EXITING      amber        — exit dispatched, awaiting fill
//   armed       ARMED        dim green    — ready to fire, waiting on final condition
//   arming      ARMING       amber        — booting/warmup, transitional
//   monitor     MONITORING   gray         — deliberately passive (carry ghosts)
//   dormant     DORMANT      dim gray     — not trading today, zero trade potential
//   blocked     BLOCKED      red          — data failure / stale heartbeat / fault

import { useQuery } from "@tanstack/react-query";
import { events, type UpcomingEvent } from "../api/endpoints";
import type { Algo } from "../api/types";

/**
 * Display state — mirrors crates/server/src/eval_vocab.rs DISPLAY_STATES +
 * the "arming" transitional grouping. Unknown engine strings degrade to
 * "blocked" here (never a silent gray "unknown").
 */
type DisplayState =
  | "long" | "short" | "managing" | "exiting" | "armed"
  | "arming" | "monitor" | "dormant" | "blocked";

const KNOWN: readonly DisplayState[] = [
  "long", "short", "managing", "exiting", "armed", "arming", "monitor", "dormant", "blocked",
];

function toDisplayState(raw: Algo["display_state"], status: Algo["status"]): DisplayState {
  if (!raw) {
    // Older engine build (field absent): fall back to status coarse truth —
    // never fabricate an Armed.
    if (status === "stopped" || status === "paused") return "dormant";
    return "arming";
  }
  return (KNOWN as readonly string[]).includes(raw) ? (raw as DisplayState) : "blocked";
}

interface AlgoSentiment {
  algo: Algo;
  intent: DisplayState;
  reason: string;
}

/**
 * Fixed group order for the summary bar and table grouping. long/short/
 * managing all mean "in a position" → one LIVE group; the row pill still
 * shows the precise state.
 */
interface Group {
  key: string;
  label: string;
  cls: string;
  states: DisplayState[];
}

const GROUPS: Group[] = [
  { key: "live", label: "Live", cls: "good", states: ["long", "short", "managing"] },
  { key: "armed", label: "Armed", cls: "dim-good", states: ["armed"] },
  { key: "arming", label: "Arming", cls: "warn", states: ["arming"] },
  { key: "exiting", label: "Exiting", cls: "warn", states: ["exiting"] },
  { key: "monitoring", label: "Monitoring", cls: "dim", states: ["monitor"] },
  { key: "dormant", label: "Dormant", cls: "faded", states: ["dormant"] },
  { key: "blocked", label: "Blocked", cls: "bad", states: ["blocked"] },
];

const GROUP_OF: Record<DisplayState, Group> = Object.fromEntries(
  GROUPS.flatMap((g) => g.states.map((s) => [s, g]))
) as Record<DisplayState, Group>;

const STATE_LABEL: Record<DisplayState, string> = {
  long: "LONG",
  short: "SHORT",
  managing: "MANAGING",
  exiting: "EXITING",
  armed: "ARMED",
  arming: "ARMING",
  monitor: "MONITORING",
  dormant: "DORMANT",
  blocked: "BLOCKED",
};

interface Props {
  algos: Algo[];
}

export function AlgoSentimentBoard({ algos }: Props) {
  const today = new Date();
  const todayStr = today.toISOString().slice(0, 10);
  const nowMinute = today.getHours() * 60 + today.getMinutes();

  const { data: apiEvents } = useQuery<UpcomingEvent[]>({
    queryKey: ["events", "upcoming"],
    queryFn: () => events.upcoming(),
    refetchInterval: 300_000,
  });

  // Classify each non-retired algo from the server-derived display state.
  const sentiments: AlgoSentiment[] = [];
  for (const algo of algos) {
    if (algo.retired) continue;
    sentiments.push({
      algo,
      intent: toDisplayState(algo.display_state, algo.status),
      reason: algo.last_eval?.note ?? "No state update from runner",
    });
  }

  // Count by group (Live merges long/short/managing)
  const counts = new Map<string, number>();
  for (const s of sentiments) {
    const g = GROUP_OF[s.intent];
    counts.set(g.key, (counts.get(g.key) ?? 0) + 1);
  }

  // Today's macro events
  const todayEvents = apiEvents?.filter((e) => e.date === todayStr) ?? [];
  const upcomingEvents = todayEvents.filter((e) => {
    if (!e.time_et) return true;
    const [h, m] = e.time_et.split(":").map(Number);
    return h * 60 + (m ?? 0) > nowMinute;
  });

  // Group sentiments in fixed display order. Every group renders in the
  // summary bar even at zero count — fixed visual anchor, the panel shape
  // is predictable morning to morning.
  const grouped = GROUPS.map((g) => ({
    group: g,
    items: sentiments.filter((s) => GROUP_OF[s.intent].key === g.key),
  }));

  return (
    <div className="card">
      <h2>Algo Sentiment</h2>
      <div className="muted small" style={{ marginBottom: 6 }}>
        {todayStr}
        {upcomingEvents.length > 0 && ` · ${upcomingEvents.length} upcoming event${upcomingEvents.length > 1 ? "s" : ""}`}
      </div>

      {/* Intent summary bar — all groups always visible, zero counts included */}
      <div className="row wrap" style={{ gap: 6, marginBottom: 8 }}>
        {GROUPS.map((g) => {
          const c = counts.get(g.key) ?? 0;
          return (
            <span
              key={g.key}
              className={`pill ${g.cls}`}
              style={{ fontSize: 10, opacity: c === 0 ? 0.35 : 1 }}
            >
              {g.label} {c}
            </span>
          );
        })}
      </div>

      {/* Upcoming macro events */}
      {upcomingEvents.length > 0 && (
        <div style={{ marginBottom: 8, padding: "4px 6px", background: "var(--bg-surface)", borderRadius: 4 }}>
          <div className="muted small" style={{ marginBottom: 4 }}>Upcoming Events</div>
          {upcomingEvents.map((ev, i) => (
            <div key={i} className="row" style={{ gap: 6, alignItems: "center", fontSize: 11, padding: "1px 0" }}>
              <span className={`pill ${ev.impact === "high" ? "bad" : "warn"}`} style={{ fontSize: 9, padding: "0 4px" }}>
                {ev.impact}
              </span>
              <span className="mono">{ev.event}</span>
              {ev.time_et && <span className="muted">{ev.time_et} ET</span>}
            </div>
          ))}
        </div>
      )}

      {/* Sleeve sentiment list — groups only when non-empty */}
      <div className="table-wrap" style={{ maxHeight: 300, overflowY: "auto" }}>
        <table>
          <thead>
            <tr>
              <th>Sleeve</th>
              <th className="num">Status</th>
              <th>Note</th>
            </tr>
          </thead>
          <tbody>
            {grouped.map(({ group, items }) =>
              items.length === 0 ? null : (
                items.map((s) => (
                  <tr key={s.algo.id}>
                    <td className="mono small" style={{ minWidth: 140 }}>{s.algo.name}</td>
                    <td className="num">
                      <span className={`pill ${group.cls}`} style={{ fontSize: 9 }}>
                        {STATE_LABEL[s.intent]}
                      </span>
                    </td>
                    <td className="small muted" style={{ maxWidth: 300, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={s.reason}>
                      {s.reason}
                    </td>
                  </tr>
                ))
              )
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
